import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getQuiz,
  updateQuiz,
  deleteQuiz,
  StoredQuiz,
  StoredQuestion,
  UpdateQuizRequest
} from '../services/quizStorageService';
import { regenerateQuestion, factCheckQuiz, FactCheckResult } from '../services/quizService';
import Header from '../components/Header';


const EditQuiz: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [quiz, setQuiz] = useState<StoredQuiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<StoredQuestion[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [factCheckResult, setFactCheckResult] = useState<FactCheckResult | null>(null);
  const [isFactChecking, setIsFactChecking] = useState(false);
  const [isFactCheckExpanded, setIsFactCheckExpanded] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    const loadQuiz = async () => {
      if (!quizId) return;

      try {
        setLoading(true);
        const fetchedQuiz = await getQuiz(quizId);
        setQuiz(fetchedQuiz);
        setTitle(fetchedQuiz.title);
        setDescription(fetchedQuiz.description);
        setQuestions(fetchedQuiz.questions);
      } catch (err) {
        console.error('Failed to load quiz:', err);
        setError(err instanceof Error ? err.message : 'Failed to load quiz');
      } finally {
        setLoading(false);
      }
    };

    loadQuiz();
  }, [quizId, isAuthenticated, navigate]);

  const handleQuestionChange = (index: number, field: keyof StoredQuestion, value: any) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setQuestions(updatedQuestions);
  };

  const handleAnswerChange = (questionIndex: number, answerIndex: number, value: string) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].answers[answerIndex] = value;
    setQuestions(updatedQuestions);
  };

  const addQuestion = () => {
    const newQuestion: StoredQuestion = {
      id: `q-${Date.now()}`,
      question: '',
      answers: ['', '', '', ''],
      correct_index: 0
    };
    setQuestions([...questions, newQuestion]);
  };

  const removeQuestion = (index: number) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    setQuestions(newQuestions);
  };

  const handleSave = async () => {
    if (!quizId) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updates: UpdateQuizRequest = {
        title,
        description,
        questions
      };

      await updateQuiz(quizId, updates);
      setSuccessMessage('Quiz saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!quizId) return;

    try {
      await deleteQuiz(quizId);
      navigate('/');
    } catch (err) {
      console.error('Failed to delete quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete quiz');
      setShowDeleteConfirm(false);
    }
  };

  const handleRegenerateQuestion = async (questionIndex: number) => {
    if (!quiz) return;

    setRegenerating(questions[questionIndex].id);
    setError(null);
    setSuccessMessage(null);

    try {
      // Prepare quiz context in the format expected by the API
      const quizContext = questions.map(q => ({
        id: q.id,
        question: q.question,
        answers: q.answers,
        correct_index: q.correct_index,
      }));

      const newQuestion = await regenerateQuestion({
        quizContext,
        questionIndex,
        difficulty: quiz.difficulty as 'easy' | 'medium' | 'hard',
        language: quiz.language,
        originalPrompt: quiz.description || quiz.title,
      });

      // Update the question in the list
      const updatedQuestions = [...questions];
      updatedQuestions[questionIndex] = {
        id: newQuestion.id,
        question: newQuestion.question,
        answers: newQuestion.alternatives,
        correct_index: newQuestion.correctAnswer,
        explanation: questions[questionIndex].explanation, // Preserve existing explanation if any
      };

      setQuestions(updatedQuestions);

      // Clear fact-check findings for this specific question
      if (factCheckResult) {
        setFactCheckResult({
          ...factCheckResult,
          findings: factCheckResult.findings.filter(
            finding => finding.question_number !== questionIndex + 1
          )
        });
      }

      setSuccessMessage('Question regenerated! Don\'t forget to save.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to regenerate question:', err);
      setError(err instanceof Error ? err.message : 'Failed to regenerate question');
    } finally {
      setRegenerating(null);
    }
  };

  const handleFactCheck = async () => {
    if (questions.length === 0) return;

    setIsFactChecking(true);
    setError(null);

    try {
      const quizContext = questions.map(q => ({
        id: q.id,
        question: q.question,
        answers: q.answers,
        correct_index: q.correct_index,
      }));

      const result = await factCheckQuiz({
        questions: quizContext,
        originalPrompt: description || title,
      });

      setFactCheckResult(result);
      setIsFactCheckExpanded(true);
      setSuccessMessage('Fact-check completed!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to fact-check quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to fact-check quiz');
    } finally {
      setIsFactChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <Header />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading quiz...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="page-container">
        <Header />
        <div className="page-content">
          <div className="card max-w-2xl mx-auto text-center">
            <div className="card-content">
              <div className="text-6xl mb-4 opacity-50">🔍</div>
              <h1 className="section-title justify-center mb-4">Quiz Not Found</h1>
              <p className="text-gray-600 mb-6">This quiz doesn't exist or you don't have access to it.</p>
              <Link to="/" className="btn-primary inline-flex">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 antialiased">
      <Header />

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        {/* Header with Back Button */}
        <div className="mb-6">
          <button onClick={() => navigate('/')} className="btn-ghost group mb-4">
            <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>

          {/* Title */}
          <div className="mb-4">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Quiz Title"
              className="text-3xl sm:text-4xl font-bold w-full border-none outline-none focus:ring-0 bg-transparent px-0 placeholder:text-gray-400"
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Quiz Description"
              rows={2}
              className="text-base text-gray-600 w-full border-none outline-none focus:ring-0 bg-transparent px-0 placeholder:text-gray-400 resize-none"
            />
          </div>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="font-medium">{questions.length} question{questions.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 rounded-xl border-l-4 border-red-500 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 rounded-xl border-l-4 border-green-500 bg-green-50 p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Fact Check Results */}
        {factCheckResult && (
          <div className={`mb-6 rounded-xl border-2 overflow-hidden transition-all ${
            factCheckResult.fact_check_passed
              ? 'bg-green-50/50 border-green-200'
              : 'bg-amber-50/50 border-amber-200'
          }`}>
            {/* Collapsible Header */}
            <button
              onClick={() => setIsFactCheckExpanded(!isFactCheckExpanded)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/40 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <svg className={`w-4 h-4 ${
                  factCheckResult.fact_check_passed ? 'text-green-600' : 'text-amber-600'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`text-sm font-semibold ${
                  factCheckResult.fact_check_passed ? 'text-green-900' : 'text-amber-900'
                }`}>
                  AI Fact Check
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  factCheckResult.fact_check_passed
                    ? 'bg-green-600 text-white'
                    : 'bg-amber-600 text-white'
                }`}>
                  {factCheckResult.fact_check_passed ? 'Verified' : 'Issues Found'}
                </span>
                <span className="text-xs text-gray-500">
                  {(factCheckResult.confidence * 100).toFixed(0)}% confidence
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  {isFactCheckExpanded ? 'Hide details' : 'View details'}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-600 transition-transform duration-300 ${
                    isFactCheckExpanded ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Expandable Details */}
            <div className={`transition-all duration-300 ease-in-out ${
              isFactCheckExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            } overflow-hidden`}>
              <div className="px-4 pb-4 pt-2 border-t border-white/50">
                {factCheckResult.fact_check_passed ? (
                  <div className="text-sm text-green-700">
                    <p className="font-medium mb-2">✓ All questions passed verification</p>
                    <p className="text-xs text-green-600">
                      Our AI reviewed all questions and answers for accuracy and quality. No issues were found.
                    </p>
                  </div>
                ) : (
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-2">⚠️ Issues Found</p>
                    {factCheckResult.findings.length > 0 && (
                      <div className="bg-white/60 rounded-lg p-3 border border-amber-200">
                        <p className="text-xs font-semibold text-amber-900 mb-2">Please review:</p>
                        <ul className="space-y-1.5">
                          {factCheckResult.findings.map((finding, idx) => (
                            <li key={idx} className="text-xs text-amber-700 flex items-start gap-2">
                              <span className="text-amber-500 mt-0.5">•</span>
                              <span className="flex-1">
                                <strong>Question {finding.question_number}:</strong> {finding.message}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons - Sticky on mobile */}
        <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4 sticky top-0 z-10 shadow-sm">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 sm:flex-initial btn-primary disabled:opacity-50"
            >
              {saving ? (
                <>
                  <span className="spinner border-white border-t-orange-200"></span>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save
                </>
              )}
            </button>
            <button
              onClick={addQuestion}
              className="flex-1 sm:flex-initial btn-secondary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </button>
            <button
              onClick={handleFactCheck}
              disabled={
                isFactChecking ||
                questions.length === 0 ||
                !['pro', 'beta'].includes(((user as any)?.['custom:plan'] ?? '') as string)
              }
              className="flex-1 sm:flex-initial btn-secondary disabled:opacity-50"
              title={user && user['custom:plan'] !== 'pro' && user['custom:plan'] !== 'beta' ? "Fact Check is a Pro feature" : "AI-powered fact checking"}
            >
              {isFactChecking ? (
                <>
                  <span className="spinner border-orange-600 border-t-orange-200"></span>
                  Checking...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Fact Check {user && user['custom:plan'] !== 'pro' && user['custom:plan'] !== 'beta' && '(Pro)'}
                </>
              )}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn-danger"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Questions List */}
        <div className="space-y-4">
          {questions.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <div className="text-6xl mb-4 opacity-50">📝</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">No Questions Yet</h2>
              <p className="text-gray-600 mb-6">Get started by adding your first question</p>
              <button onClick={addQuestion} className="btn-primary">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Your First Question
              </button>
            </div>
          ) : (
            questions.map((question, qIndex) => {
              // Check if this question has findings in fact-check
              const questionFinding = factCheckResult?.findings.find(
                finding => finding.question_number === qIndex + 1
              );

              return (
                <div key={question.id} className={`bg-white rounded-lg border p-4 sm:p-6 transition-all ${
                  questionFinding
                    ? 'bg-amber-50/30 border-amber-300 hover:border-amber-400'
                    : 'border-gray-200 hover:border-orange-300'
                }`}>
                  {/* Fact Check Warning */}
                  {questionFinding && (
                    <div className="mb-3 flex items-start gap-2 bg-amber-100/50 border border-amber-300 rounded-lg p-2">
                      <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs sm:text-sm text-amber-800 font-medium">
                          AI Fact Check: Issue Found
                        </p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          {questionFinding.message}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Question Header */}
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-orange-600 mb-2">Question {qIndex + 1}</div>
                      <textarea
                        value={question.question}
                        onChange={(e) => handleQuestionChange(qIndex, 'question', e.target.value)}
                        placeholder="Enter your question..."
                        className="w-full text-lg font-semibold text-gray-900 border-none outline-none focus:ring-0 bg-transparent resize-none placeholder:text-gray-400"
                        rows={2}
                      />
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleRegenerateQuestion(qIndex)}
                      disabled={regenerating === question.id}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all disabled:opacity-50"
                      title="Regenerate - AI creates a new question that fits with your quiz theme"
                    >
                      {regenerating === question.id ? (
                        <span className="spinner border-blue-600 border-t-transparent w-5 h-5"></span>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => removeQuestion(qIndex)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete question"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Regenerate Helper Text */}
                {regenerating === question.id && (
                  <div className="mb-4 text-sm text-blue-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    AI is creating a new question that fits your quiz theme...
                  </div>
                )}

                {/* Answer Options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {question.answers.map((answer, aIndex) => {
                    const isCorrect = question.correct_index === aIndex;
                    const labels = ['A', 'B', 'C', 'D'];

                    return (
                      <div key={aIndex} className="relative">
                        <button
                          onClick={() => handleQuestionChange(qIndex, 'correct_index', aIndex)}
                          className={`relative w-full p-4 rounded-lg border-2 transition-all text-left ${
                            isCorrect
                              ? 'bg-green-50 border-green-500 ring-2 ring-green-200'
                              : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className={`absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            isCorrect ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-700'
                          }`}>
                            {labels[aIndex]}
                          </div>
                          <textarea
                            value={answer}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleAnswerChange(qIndex, aIndex, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            placeholder={`Option ${labels[aIndex]}`}
                            rows={1}
                            className={`w-full pl-8 pr-2 py-1 border-none outline-none focus:ring-0 text-sm font-medium resize-none overflow-hidden ${
                              isCorrect
                                ? 'bg-green-50 text-green-900 placeholder:text-green-400'
                                : 'bg-gray-50 text-gray-900 placeholder:text-gray-400'
                            }`}
                            style={{
                              minHeight: '2rem',
                              height: 'auto'
                            }}
                            onInput={(e) => {
                              const target = e.target as HTMLTextAreaElement;
                              target.style.height = 'auto';
                              target.style.height = target.scrollHeight + 'px';
                            }}
                          />
                          {isCorrect && (
                            <svg className="absolute top-2 right-2 w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md transform animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Delete Quiz?
                </h3>
                <p className="text-sm text-gray-500 mt-1">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6 pl-16">
              Are you sure you want to permanently delete "<strong>{title}</strong>"? All {questions.length} question{questions.length !== 1 ? 's' : ''} will be lost.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 btn-danger"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Quiz
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditQuiz;